import { useState, useEffect } from 'react';
import { api } from '@/services/api';
import { FunnelAnalytics } from '@/types';
import { Filter, RefreshCw, AlertCircle, TrendingDown, Users, Activity } from 'lucide-react';

const WINDOW_OPTIONS: { value: number | 'all'; label: string }[] = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

export default function FunnelAnalyticsPage() {
  const [data, setData] = useState<FunnelAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState<number | 'all'>(30);

  const fetchData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await api.getFunnelAnalytics({ days });
      setData(res);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load funnel analytics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const startCount = data?.steps?.[0]?.count ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Journey Funnel</h1>
          <p className="text-gray-500 mt-1">Where travellers drop off, from the first city search through to payment</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={String(days)}
              onChange={(e) => {
                const v = e.target.value;
                setDays(v === 'all' ? 'all' : Number(v));
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? null : !data || startCount === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No funnel activity</h3>
          <p className="mt-2 text-gray-500">
            No anonymous funnel sessions were started in this time window.
          </p>
        </div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sessions started</p>
                  <p className="text-2xl font-bold text-gray-900">{startCount.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Paid conversion</p>
                  <p className="text-2xl font-bold text-gray-900">{data.overallConversionPct}%</p>
                  {data.signupConversionPct != null && (
                    <p className="text-xs text-gray-500">{data.signupConversionPct}% signed up</p>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-5">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Avg readiness score</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.avgReadinessScore != null ? data.avgReadinessScore : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Funnel bars */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="space-y-5">
              {data.steps.map((step, idx) => (
                <div key={step.key}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-900">
                      {idx + 1}. {step.label}
                    </span>
                    <span className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">
                        {step.count.toLocaleString()}
                      </span>{' '}
                      ({step.pctOfStart}% of start)
                    </span>
                  </div>
                  <div className="relative h-8 bg-gray-100 rounded-md overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-primary-500 rounded-md transition-all"
                      style={{ width: `${Math.max(step.pctOfStart, 1.5)}%` }}
                    />
                  </div>
                  {idx > 0 && (
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <TrendingDown size={13} className="text-red-400" />
                      <span>
                        {step.droppedFromPrevious.toLocaleString()} dropped off from previous step
                      </span>
                      <span className="text-gray-300">•</span>
                      <span>{step.pctOfPrevious}% advanced</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            Generated {new Date(data.generatedAt).toLocaleString()} ·{' '}
            {data.windowDays == null ? 'All time' : `Last ${data.windowDays} days`}
          </p>
        </>
      )}
    </div>
  );
}
